import type { FieldSpec, SortSpec } from '@moment/horizon-data-access/platform'

export const SORM_PAGE_SIZE = 10_000

export const DEFAULT_ACCOUNT_LIST_LIMIT = 100

export const ACCOUNT_QUERY_SORM_QUERY_NAMES = {
  accountList: 'legacy_account_list',
  accountIds: 'legacy_account_ids',
  abbreviatedAccountList: 'legacy_abbreviated_account_list',
  accountStatuses: 'legacy_account_statuses',
  accountSleeves: 'legacy_account_sleeves',
  positionsForInstrument: 'legacy_positions_for_instrument',
} as const

// Public account field name -> SORM Account field name, where they differ.
export const SORM_ACCOUNT_FIELD_BY_PUBLIC_FIELD: Readonly<
  Record<string, string>
> = {
  moment_account_id: 'id',
  account_type: 'account_type_code',
  prime_brokerage_enabled: 'prime_brokerage_eligible',
  tracked_fixed_income_market_value: 'tracked_fi_market_value',
  average_ytw: 'average_yield_to_worst',
  is_taxable: 'tax_status',
  custodian_status: 'account_status',
  moment_organization_id: 'organization_id',
  percent_uninvested_cash: 'uninvested_cash_pct',
}

export const DEFAULT_ACCOUNT_SORT: SortSpec = {
  entity: 'Account',
  field: 'account_name',
  direction: 'asc',
}

export const SYNC_STATUS_BY_MOMENT_STATUS: Readonly<Record<string, string>> = {
  STATUS_RUNNING: 'Synced',
  STATUS_PAUSED: 'Stale',
  running: 'Synced',
  paused: 'Stale',
}

export const MOMENT_STATUS_BY_SYNC_STATUS: Readonly<Record<string, string>> = {
  Synced: 'running',
  Stale: 'paused',
}

export const NO_MATCH_FILTER_VALUE = '__no_match__'

export const TAXABLE_TAX_STATUS = 'TAXABLE'

export const NON_TAXABLE_TAX_STATUSES = ['NON_TAXABLE', 'TAX_DEFERRED']

// Ranks match the labels horizon's account statistics view derives from
// `average_*_rating_rank` (1-based: S&P D=1..AAA=22, Moody's C=1..Aaa=21).
export const SP_RATING_RANKS: Readonly<Record<string, number>> = {
  AAA: 22,
  'AA+': 21,
  AA: 20,
  'AA-': 19,
  'A+': 18,
  A: 17,
  'A-': 16,
  'BBB+': 15,
  BBB: 14,
  'BBB-': 13,
  'BB+': 12,
  BB: 11,
  'BB-': 10,
  'B+': 9,
  B: 8,
  'B-': 7,
  'CCC+': 6,
  CCC: 5,
  'CCC-': 4,
  CC: 3,
  C: 2,
  D: 1,
}

export const MOODYS_RATING_RANKS: Readonly<Record<string, number>> = {
  Aaa: 21,
  Aa1: 20,
  Aa2: 19,
  Aa3: 18,
  A1: 17,
  A2: 16,
  A3: 15,
  Baa1: 14,
  Baa2: 13,
  Baa3: 12,
  Ba1: 11,
  Ba2: 10,
  Ba3: 9,
  B1: 8,
  B2: 7,
  B3: 6,
  Caa1: 5,
  Caa2: 4,
  Caa3: 3,
  Ca: 2,
  C: 1,
}

// Account filter keys the legacy GraphQL schema accepts, grouped by how each
// translates into a SORM filter.
export const STRING_ACCOUNT_FILTER_KEYS = [
  'moment_account_id',
  'custodian_account_id',
  'account_name',
  'account_type',
  'tax_state',
  'custodian_id',
  'household_id',
  'custodian_status',
  'moment_organization_id',
  'advisor_id',
  'advisor_name',
  'do_not_trade_reason',
] as const

export const BOOLEAN_ACCOUNT_FILTER_KEYS = [
  'do_not_trade',
  'prime_brokerage_enabled',
] as const

export const NUMERICAL_ACCOUNT_FILTER_KEYS = [
  'total_market_value',
  'cash_market_value',
  'other_market_value',
  'tracked_fi_market_value',
  'total_accrued_interest',
  'average_coupon',
  'average_yield_to_worst',
  'average_maturity_in_years',
  'average_duration',
  'percent_uninvested_cash',
] as const

function accountField(field: string, alias?: string): FieldSpec {
  return alias
    ? { entity: 'Account', field, alias }
    : { entity: 'Account', field }
}

const ACCOUNT_ID_FIELD = accountField('id', 'moment_account_id')

const ACCOUNT_STATUS_FIELDS: FieldSpec[] = [
  ACCOUNT_ID_FIELD,
  accountField('do_not_trade'),
  accountField('do_not_trade_reason'),
]

const ACCOUNT_MARGIN_FIELDS: FieldSpec[] = [
  accountField('do_not_buy'),
  accountField('do_not_sell'),
]

export const ACCOUNT_STATUS_SORM_FIELDS = ACCOUNT_STATUS_FIELDS

export const ACCOUNT_STATUS_WITH_MARGIN_SORM_FIELDS = [
  ...ACCOUNT_STATUS_FIELDS,
  ...ACCOUNT_MARGIN_FIELDS,
]

export const ACCOUNT_IDS_SORM_FIELDS: FieldSpec[] = [
  ACCOUNT_ID_FIELD,
  accountField('account_name'),
]

const ACCOUNT_LIST_FIELDS: FieldSpec[] = [
  ...ACCOUNT_STATUS_FIELDS,
  accountField('organization_id', 'moment_organization_id'),
  accountField('custodian_id'),
  accountField('custodian_name'),
  accountField('custodian_account_id'),
  accountField('sync_status'),
  accountField('account_status', 'custodian_status'),
  accountField('risk_group_id'),
  accountField('account_type_code', 'account_type'),
  accountField('tax_state'),
  accountField('account_name'),
  accountField('notes'),
  accountField('money_manager'),
  accountField('advisor_id'),
  accountField('advisor_name'),
  accountField('prime_brokerage_eligible', 'prime_brokerage_enabled'),
  accountField('total_market_value'),
  accountField('cash_market_value'),
  accountField('tracked_fi_market_value', 'tracked_fixed_income_market_value'),
  accountField('uninvested_cash_pct', 'percent_uninvested_cash'),
  accountField('average_yield_to_worst', 'average_ytw'),
  accountField('average_maturity_in_years'),
  accountField('average_moodys_rating'),
  accountField('average_sp_rating'),
  accountField('average_coupon'),
  accountField('average_duration'),
]

export const ACCOUNT_LIST_SORM_FIELDS = ACCOUNT_LIST_FIELDS

export const ACCOUNT_LIST_WITH_MARGIN_SORM_FIELDS = [
  ...ACCOUNT_LIST_FIELDS,
  ...ACCOUNT_MARGIN_FIELDS,
]

export const ABBREVIATED_ACCOUNT_SORM_FIELDS: FieldSpec[] = [
  ...ACCOUNT_STATUS_FIELDS,
  accountField('custodian_account_id'),
  accountField('account_name'),
  accountField('account_type_code', 'account_type'),
  accountField('household_id'),
  accountField('sync_status'),
  accountField('risk_group_id'),
  accountField('custodian_id'),
  accountField('custodian_name'),
  accountField('prime_brokerage_eligible', 'prime_brokerage_enabled'),
  accountField('notes'),
  accountField('money_manager'),
  accountField('cash_market_value'),
]

export const ABBREVIATED_ACCOUNT_STATISTICS_SORM_FIELDS: FieldSpec[] = [
  accountField('total_market_value'),
]

export const ABBREVIATED_ACCOUNT_MARGIN_SORM_FIELDS: FieldSpec[] = [
  accountField('margin_indicator'),
  ...ACCOUNT_MARGIN_FIELDS,
]

export const SLEEVE_SORM_FIELDS: FieldSpec[] = [
  { entity: 'Sleeve', field: 'id', alias: 'moment_sleeve_id' },
  { entity: 'Sleeve', field: 'account_id' },
  { entity: 'Sleeve', field: 'name' },
  { entity: 'Sleeve', field: 'is_default' },
  { entity: 'Sleeve', field: 'target_allocation' },
  { entity: 'Sleeve', field: 'strategy_id' },
  { entity: 'Sleeve', field: 'strategy_name' },
]

export const POSITIONS_FOR_INSTRUMENT_SORM_FIELDS: FieldSpec[] = [
  accountField('id', 'account_id'),
  accountField('account_name'),
  accountField('advisor_id'),
  accountField('advisor_name'),
  { entity: 'Position', field: 'market_value' },
  { entity: 'Position', field: 'principal_value' },
  { entity: 'Position', field: 'settled_principal_value' },
  { entity: 'Position', field: 'total_cost', alias: 'basis_market_value' },
  { entity: 'Position', field: 'unrealized_gain_loss', alias: 'gain_loss' },
]
